import type {
  CreateOneRequestType,
  CreateOneResponseType,
  GetListRequestType,
  GetListResponseType,
  GetOneRequestType,
  GetOneResponseType,
  ResourceAdapterToAdapterQueryKeysType,
  UpdateOneRequestType,
  UpdateOneResponseType,
} from "../util/index.ts";
import {
  fetchAndCollate,
  getFilterObject,
  Resources,
  SortDirection,
} from "../util/index.ts";
import type {
  CreateInvestigationBodyType,
  CreateInvestigationMessageBodyType,
  CreateInvestigationMessageResponseType,
  CreateInvestigationResponseType,
  InvestigationDetailType,
  InvestigationGetListRequestType,
  InvestigationMessageType,
  InvestigationType,
  ListInvestigationMessagesResponseType,
  ListInvestigationsResponseType,
  UpdateInvestigationBodyType,
} from "../schemas/InvestigationSchemas.ts";
import { restAPI } from "./api.ts";

const INVESTIGATIONS_PATH = "/investigations";

/**
 * Carbon Hub investigations API (`/v1/investigations`).
 *
 * Staging OpenAPI (Swagger UI):
 * - {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/search_investigations | search_investigations} — `POST /v1/investigations/search`
 * - {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/list_investigations | list_investigations} — `GET /v1/investigations` (deprecated for filtered/paginated listing)
 * - {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/get_investigation | get_investigation} — `GET /v1/investigations/{id}`
 * - {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/create_investigation | create_investigation} — `POST /v1/investigations`
 * - {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/update_investigation | update_investigation} — `PUT /v1/investigations/{id}`
 * - {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/create_investigation_message | create_investigation_message} — `POST /v1/investigations/{id}/messages`
 *
 * @see {@link https://github.com/ValidereInc/Carbon-Hub-Backend/blob/master/openapi/swagger.yaml | OpenAPI source (`operationId`)}
 * @see {@link https://github.com/ValidereInc/Carbon-Hub-Backend/tree/master/lambda/investigations | Lambda implementation}
 */
export const InvestigationAdapter: InvestigationAdapterType = {
  /** @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/search_investigations | search_investigations} */
  getList: ({
    page = 1,
    pageSize = 20,
    sortBy = "created_at",
    sortDirection = SortDirection.DESCENDING,
    filters,
  }: InvestigationGetListRequestType) =>
    fetchAndCollate<InvestigationType>(
      ({ page: pageNumber, pageSize: pageSizeNumber }) =>
        restAPI.nodeAPI.POST<ListInvestigationsResponseType>({
          endpoint: `${INVESTIGATIONS_PATH}/search`,
          body: {
            page: pageNumber,
            page_size: pageSizeNumber,
            sort_by: sortBy,
            sort_direction: sortDirection,
            filter: getFilterObject(filters),
          },
        }),
      page,
      pageSize
    ),

  /** @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/get_investigation | get_investigation} */
  getOne: async ({ id }) => {
    const resp = await restAPI.nodeAPI.GET<
      InvestigationType & { messages?: InvestigationMessageType[] }
    >({
      endpoint: `${INVESTIGATIONS_PATH}/${id}`,
    });
    const messages =
      "messages" in resp && Array.isArray(resp.messages) ? resp.messages : [];
    return {
      data: { ...resp, messages },
    };
  },

  /** @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/create_investigation | create_investigation} */
  createOne: async ({ data }) => {
    const resp = await restAPI.nodeAPI.POST<CreateInvestigationResponseType>({
      endpoint: INVESTIGATIONS_PATH,
      body: data,
    });
    return { data: resp };
  },

  /** @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/update_investigation | update_investigation} */
  updateOne: async ({ id, data }) => {
    const resp = await restAPI.nodeAPI.PUT<InvestigationType>({
      endpoint: `${INVESTIGATIONS_PATH}/${id}`,
      body: data,
    });
    return { data: resp };
  },

  messages: {
    /**
     * Paginated list of messages for an investigation. The agent writes new
     * turns asynchronously, so this is polled while the parent investigation
     * is still in a non-terminal status.
     *
     * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/list_investigation_messages | list_investigation_messages}
     */
    getList: async ({ meta, page = 1, pageSize = 20 }) => {
      const response: GetListResponseType<InvestigationMessageType> =
        await restAPI.nodeAPI.GET<ListInvestigationMessagesResponseType>({
          endpoint: `${INVESTIGATIONS_PATH}/${meta.id}/messages`,
          query: {
            page,
            page_size: pageSize,
            sort_by: "created_at",
            sort_direction: "desc",
          },
        });
      return response;
    },

    /** @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/investigations/create_investigation_message | create_investigation_message} */
    createOne: async (params) => {
      const investigationId = params.meta.id;
      const resp =
        await restAPI.nodeAPI.POST<CreateInvestigationMessageResponseType>({
          endpoint: `${INVESTIGATIONS_PATH}/${investigationId}/messages`,
          body: params.data,
        });
      return { data: resp };
    },
  },
};

/**
 * Investigations adapter — same *shape* as {@link ResourceAdapterType} for list/get,
 * but `createOne` returns only `{ investigation_id }` per API contract (not a full row).
 */
export interface InvestigationAdapterType {
  getList: (
    params: InvestigationGetListRequestType
  ) => Promise<GetListResponseType<InvestigationType>>;
  getOne: (
    params: GetOneRequestType
  ) => Promise<GetOneResponseType<InvestigationDetailType>>;
  createOne: (
    params: CreateOneRequestType<CreateInvestigationBodyType>
  ) => Promise<CreateOneResponseType<CreateInvestigationResponseType>>;
  updateOne: (
    params: UpdateOneRequestType<UpdateInvestigationBodyType>
  ) => Promise<UpdateOneResponseType<InvestigationType>>;
  messages: {
    getList: (
      params: GetListRequestType<unknown, Pick<InvestigationType, "id">> & {
        meta: Pick<InvestigationType, "id">;
      }
    ) => Promise<GetListResponseType<InvestigationMessageType>>;
    createOne: (
      params: CreateOneRequestType<
        CreateInvestigationMessageBodyType,
        Pick<InvestigationType, "id">
      > & { meta: Pick<InvestigationType, "id"> }
    ) => Promise<CreateOneResponseType<CreateInvestigationMessageResponseType>>;
  };
}

export const InvestigationAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<InvestigationAdapterType> =
  {
    getList: (params) => [Resources.INVESTIGATION, "getList", params] as const,
    getOne: (params) => [Resources.INVESTIGATION, "getOne", params.id] as const,
    createOne: (params) =>
      [Resources.INVESTIGATION, "createOne", params] as const,
    updateOne: (params) =>
      [Resources.INVESTIGATION, "updateOne", params.id] as const,
    messages: {
      // Order matters for invalidation: putting `id` before `{ page, pageSize }`
      // lets callers invalidate every page of a given investigation's messages
      // with `[INVESTIGATION, "messages", "getList", id]`.
      getList: (params) =>
        [
          Resources.INVESTIGATION,
          "messages",
          "getList",
          params.meta.id,
          { page: params.page, pageSize: params.pageSize },
        ] as const,
      createOne: (params) =>
        [
          Resources.INVESTIGATION,
          "messages",
          "createOne",
          params.meta.id,
        ] as const,
    },
  };
